// Read-model derived from a property offer payload (summary, deadlines, money rows,
// contract/KYC snapshots and contact shortcuts). Pure functions extracted verbatim
// from RentFlowsIndex.vue so the drawer/card copy stays byte-identical.
import {
  DEFAULT_FINANCING_ANNUAL_RATE,
  DEFAULT_FINANCING_TERM_MONTHS,
  buildPurchasePaymentInfo,
} from 'dashboard/helper/lais/paymentInfo';
import { kycVerificationsForOffer } from './kycOfferHistory';
import { valueFrom } from './offerCatalogItem';
import { isFinancingPayment, paymentLabel } from './offerFormOptions';
import {
  activityDurationLabel,
  daysUntilDeadline,
  formatDate,
  formatDeadlineDate,
  numericValue,
  residentsSituationFromValues,
  valuesAreEquivalent,
} from './offerFormatters';

export const offerContactId = offer =>
  offer?.contact_id || offer?.contact?.id || null;

export const offerImovelId = offer =>
  valueFrom(offer, 'imovel_id', 'imovelId') ||
  offer?.imovel?.id ||
  offer?.property?.id;

export const offerSummary = offer => offer?.summary || {};

export const offerResponseDeadline = offer =>
  offerSummary(offer).response_deadline_date ||
  offer?.metadata?.response_deadline_date ||
  offer?.metadata?.response_deadline_at ||
  offer?.metadata?.supply_deadline_date ||
  offer?.metadata?.supply_deadline_at ||
  '';

export const offerDeadlineStatus = offer => {
  if (!offer || offer.status !== 'submitted') return '';
  const deadline = offerResponseDeadline(offer);
  const days = daysUntilDeadline(deadline);
  if (days === null) return 'Para responder';
  if (days <= 0) return 'Responder até hoje';
  if (days === 1) return 'Responder até amanhã';
  return `Responder até ${formatDeadlineDate(deadline)}`;
};

export const offerDeadlineClass = offer => {
  if (!offer || offer.status !== 'submitted') return 'text-lais-text-muted';
  const days = daysUntilDeadline(offerResponseDeadline(offer));
  if (days === null) return 'text-lais-text-muted';
  if (days <= 0) return 'text-rose-700';
  return 'text-lais-primary';
};

export const contractDeadlineValue = offer =>
  offer?.metadata?.contract_deadline_date ||
  offer?.metadata?.contract_deadline_at ||
  offer?.metadata?.signature_deadline_date ||
  offer?.metadata?.signature_deadline_at ||
  '';

export const contractDeadlineStatus = offer => {
  const deadline = contractDeadlineValue(offer);
  const days = daysUntilDeadline(deadline);
  if (days === null) return 'Assinar contrato agora';
  if (days <= 0) return 'Assinar até hoje';
  if (days === 1) return 'Assinar até amanhã';
  return `Assinar até ${formatDeadlineDate(deadline)}`;
};

export const contractStartDateValue = offer =>
  offer?.metadata?.contract_start_date ||
  offer?.metadata?.contract_start_at ||
  offer?.metadata?.lease_start_date ||
  offer?.metadata?.lease_start_at ||
  offer?.metadata?.rent_start_date ||
  offer?.metadata?.start_date ||
  '';

export const contractForOffer = offer => {
  if (offer?.ccv_contract) return offer.ccv_contract;
  return Array.isArray(offer?.property_contracts)
    ? offer.property_contracts.find(contract => contract.kind === 'ccv') || null
    : null;
};

export const isContractSent = contract =>
  ['sent', 'partially_signed', 'signed'].includes(contract?.status);

export const isContractSigned = contract => contract?.status === 'signed';

export const kycForOffer = offer => kycVerificationsForOffer(offer)[0] || null;

export const isKycApproved = verification =>
  verification?.status === 'approved';

export const contractSignatureDescription = offer => {
  const contract = contractForOffer(offer);
  if (isContractSigned(contract)) {
    return 'CCV assinado por todos os envolvidos.';
  }
  if (isContractSent(contract)) {
    return 'CCV enviado para assinatura digital. Acompanhe os links dos signatários.';
  }
  if (!contractDeadlineValue(offer)) {
    return 'O contrato será enviado por e-mail em alguns instantes...';
  }

  const contractStartDate = contractStartDateValue(offer);
  return [
    'O contrato está pronto para assinar e foi enviado por e-mail!',
    contractDeadlineStatus(offer),
    contractStartDate
      ? `A data de início do contrato será em ${formatDeadlineDate(contractStartDate)}.`
      : '',
  ]
    .filter(Boolean)
    .join(' ');
};

export const contractDeadlineClass = offer => {
  const days = daysUntilDeadline(contractDeadlineValue(offer));
  if (days !== null && days <= 0) return 'text-rose-700';
  return 'text-lais-primary';
};

export const buyerLabel = offer =>
  offer.contact_name ||
  offer.contact_email ||
  offer.contact_phone_number ||
  'Cliente não vinculado';

export const offerConversationId = offer =>
  offer?.conversation_display_id ||
  offer?.conversationDisplayId ||
  offer?.conversation?.display_id ||
  offer?.conversation?.displayId ||
  offer?.conversation_id ||
  offer?.conversation?.id ||
  offer?.latest_conversation_id ||
  null;

export const ownerContactFromOffer = offer =>
  offer?.imovel?.owner_contact ||
  offer?.owner_contact ||
  offer?.metadata?.owner_contact ||
  {};

export const ownerWhatsappHrefForOffer = offer => {
  const owner = ownerContactFromOffer(offer);
  const phone =
    owner.phone_number || owner.phoneNumber || offer?.metadata?.owner_phone;
  const digits = String(phone || '').replace(/\D+/g, '');
  if (!digits) return '';

  const text = `Olá! Estou entrando em contato sobre a proposta do imóvel ${offer?.imovel_titulo || ''}.`;
  return `https://wa.me/55${digits.replace(/^55/, '')}?text=${encodeURIComponent(
    text
  )}`;
};

export const whatsappHrefForOffer = offer => {
  const digits = String(offer?.contact_phone_number || '').replace(/\D+/g, '');
  if (!digits) return '';
  const text = `Olá, ${buyerLabel(offer)}! Estou entrando em contato sobre a proposta do imóvel ${offer?.imovel_titulo || ''}.`;
  return `https://wa.me/55${digits.replace(/^55/, '')}?text=${encodeURIComponent(
    text
  )}`;
};

export const isOccupiedOffer = offer =>
  Boolean(
    offer?.imovel_ocupado ||
      offer?.imovel_has_resident ||
      offer?.metadata?.house_with_resident ||
      offer?.metadata?.occupied
  );

export const offerReference = offer =>
  offer?.booking_reference ||
  (offer?.public_link?.token_last_four
    ? `link-${offer.public_link.token_last_four}`
    : offer?.id) ||
  '—';

export const isRentalOffer = offer =>
  offer?.business_context === 'rental' ||
  offer?.imovel_finalidade === 'aluguel';

export const offerStatusDetail = offer => {
  const summaryMessage = offerSummary(offer).status_message;
  if (summaryMessage) return summaryMessage;
  if (isRentalOffer(offer) && offer.status === 'accepted') {
    return contractDeadlineStatus(offer);
  }
  return offerDeadlineStatus(offer);
};

export const offerStatusDetailClass = offer => {
  if (isRentalOffer(offer) && offer.status === 'accepted') {
    return contractDeadlineClass(offer);
  }
  return offerDeadlineClass(offer);
};

export const advertisedValue = offer =>
  isRentalOffer(offer)
    ? numericValue(offer?.imovel_valor_aluguel)
    : numericValue(offer?.imovel_valor_venda);

export const offerAmount = offer => numericValue(offer?.offer_amount);

export const offerAmountLabel = offer => {
  const summaryLabel = offerSummary(offer).amount_label;
  if (summaryLabel) return summaryLabel;

  if (valuesAreEquivalent(offerAmount(offer), advertisedValue(offer))) {
    return 'Proposta no valor do anúncio';
  }

  return isRentalOffer(offer) ? 'Proposta de aluguel' : 'Proposta de venda';
};

export const offerPaymentBreakdown = offer =>
  offer?.metadata?.payment_breakdown || offer?.metadata?.paymentBreakdown || {};

export const offerRentalBreakdown = offer =>
  offer?.metadata?.rental_breakdown || offer?.metadata?.rentalBreakdown || {};

export const metadataNumber = (offer, key) => {
  const value = offerPaymentBreakdown(offer)?.[key];
  return numericValue(value);
};

export const metadataNumberFromKeys = (offer, ...keys) => {
  const key = keys.find(currentKey => {
    const value = metadataNumber(offer, currentKey);
    return value !== null;
  });
  return key ? metadataNumber(offer, key) : null;
};

export const metadataBoolean = (offer, key) => {
  const value = offerPaymentBreakdown(offer)?.[key];
  return value === true || value === 'true';
};

export const rentalMetadataNumber = (offer, key) => {
  const value = offerRentalBreakdown(offer)?.[key];
  return numericValue(value);
};

export const rentalMetadataBoolean = (offer, key) => {
  const value = offerRentalBreakdown(offer)?.[key];
  return value === true || value === 'true';
};

export const brokerageFeeValue = offer => {
  const metadataValue = metadataNumber(offer, 'brokerage_fee_value');
  if (metadataValue != null) return metadataValue;
  const amount = offerAmount(offer);
  return amount == null ? 0 : amount * 0.06;
};

export const purchasePaymentInfoForOffer = offer =>
  buildPurchasePaymentInfo({
    propertyValue: offerAmount(offer),
    paymentKind: offer?.payment_kind,
    uf: offer?.imovel_uf || offer?.uf,
    city: offer?.imovel_cidade || offer?.cidade,
    firstPurchase:
      offer?.first_purchase || metadataBoolean(offer, 'first_purchase'),
    hasSfh: offer?.has_sfh || metadataBoolean(offer, 'has_sfh'),
    itbiValue: metadataNumber(offer, 'itbi_amount'),
    itbiOverridden: metadataBoolean(offer, 'itbi_overridden'),
    notaryFeeValue: metadataNumber(offer, 'notary_fee_value'),
    notaryFeeOverridden: metadataBoolean(offer, 'notary_fee_overridden'),
    brokerageFeeValue: brokerageFeeValue(offer),
    financedAmount:
      offer?.financed_amount || metadataNumber(offer, 'financed_amount'),
    termMonths:
      metadataNumber(offer, 'financing_term_months') ||
      DEFAULT_FINANCING_TERM_MONTHS,
    annualRate:
      metadataNumber(offer, 'financing_annual_rate') ||
      DEFAULT_FINANCING_ANNUAL_RATE,
  });

export const rentalAdminFeeForOffer = offer => {
  const metadataValue = rentalMetadataNumber(offer, 'admin_fee_value');
  if (metadataValue != null) return metadataValue;

  const amount = offerAmount(offer);
  if (amount == null) return null;

  const percentage = rentalMetadataNumber(offer, 'admin_fee_percentage') ?? 10;
  const minimum = rentalMetadataNumber(offer, 'minimum_fee_value') ?? 0;
  return Math.max((amount * percentage) / 100, minimum);
};

export const ownerReceiveValue = offer => {
  const amount = offerAmount(offer);
  if (amount == null) return null;
  if (!isRentalOffer(offer)) {
    return Math.max(amount - brokerageFeeValue(offer), 0);
  }
  const metadataValue = rentalMetadataNumber(offer, 'owner_receive_amount');
  if (metadataValue != null) return metadataValue;

  return Math.max(amount - (rentalAdminFeeForOffer(offer) || 0), 0);
};

export const offerPaymentLabel = offer => {
  const summaryLabel = offerSummary(offer).payment_label;
  if (summaryLabel) return summaryLabel;

  return isRentalOffer(offer)
    ? offerRentalBreakdown(offer)?.residents_situation ||
        residentsSituationFromValues(
          rentalMetadataNumber(offer, 'residents_count'),
          rentalMetadataBoolean(offer, 'has_pets')
        ) ||
        'Detalhes da proposta'
    : paymentLabel(offer?.payment_kind);
};

export const rentalIptuValue = offer =>
  rentalMetadataNumber(offer, 'iptu_amount') ??
  numericValue(offer?.imovel_valor_iptu);

export const rentalCondominiumValue = offer =>
  rentalMetadataNumber(offer, 'condominium_amount') ??
  numericValue(offer?.imovel_valor_condominio);

export const rentalAdminFeePercentageLabel = offer => {
  const percentage = rentalMetadataNumber(offer, 'admin_fee_percentage') ?? 10;
  return `Taxa de adm. (${percentage}%)`;
};

export const valueRowsForOffer = offer => {
  if (!offer) return [];

  const summaryRows = Array.isArray(offerSummary(offer).values)
    ? offerSummary(offer).values
    : [];
  if (summaryRows.length) {
    return summaryRows.filter(
      row => row?.label && (row.advertised != null || row.proposal != null)
    );
  }

  if (isRentalOffer(offer)) {
    return [
      {
        label: 'Aluguel',
        advertised: offer.imovel_valor_aluguel,
        proposal: offer.offer_amount,
      },
      {
        label: 'IPTU',
        advertised: offer.imovel_valor_iptu,
        proposal: rentalIptuValue(offer),
      },
      {
        label: 'Condomínio',
        advertised: offer.imovel_valor_condominio,
        proposal: rentalCondominiumValue(offer),
      },
      {
        label: rentalAdminFeePercentageLabel(offer),
        advertised: null,
        proposal: rentalAdminFeeForOffer(offer),
      },
      {
        label: 'Taxa mínima de adm.',
        advertised: null,
        proposal: rentalMetadataNumber(offer, 'minimum_fee_value'),
      },
      {
        label: 'Proprietário vai receber',
        advertised: null,
        proposal: ownerReceiveValue(offer),
        highlight: true,
      },
    ];
  }

  const paymentInfo = purchasePaymentInfoForOffer(offer);

  return [
    {
      label: 'Valor do anúncio',
      advertised: advertisedValue(offer),
      proposal: advertisedValue(offer),
    },
    {
      label: 'Valor final da venda',
      advertised: null,
      proposal: offer.offer_amount,
      highlight: true,
    },
    {
      label: 'Valor total da corretagem',
      advertised: null,
      proposal: brokerageFeeValue(offer),
    },
    {
      label: 'ITBI estimado',
      advertised: null,
      proposal: paymentInfo.fees.itbi,
    },
    {
      label: 'Cartório e registro',
      advertised: null,
      proposal: paymentInfo.fees.notary,
    },
    {
      label: 'Sinal',
      advertised: null,
      proposal: metadataNumber(offer, 'earnest_payment_amount'),
    },
    {
      label: 'Entrada',
      advertised: null,
      proposal: metadataNumberFromKeys(offer, 'entry_amount', 'cash_amount'),
    },
    {
      label: 'Valor de FGTS',
      advertised: null,
      proposal: metadataNumber(offer, 'fgts_amount'),
    },
    {
      label: 'Valor financiado',
      advertised: null,
      proposal: isFinancingPayment(offer.payment_kind)
        ? offer.financed_amount || metadataNumber(offer, 'financed_amount')
        : null,
    },
    {
      label: 'Proprietário vai receber',
      advertised: null,
      proposal: ownerReceiveValue(offer),
      highlight: true,
    },
    {
      label: 'Custo total estimado',
      advertised: null,
      proposal: paymentInfo.total,
      highlight: true,
    },
  ];
};

export const customSolicitations = offer => {
  const summarySolicitations = Array.isArray(
    offerSummary(offer).custom_solicitations
  )
    ? offerSummary(offer).custom_solicitations
    : [];
  if (summarySolicitations.length) {
    return summarySolicitations.map(item => ({
      type: item.label || item.type || 'Modificar',
      description: item.description || item.message || item.label,
    }));
  }

  const raw =
    offerRentalBreakdown(offer)?.custom_solicitations ||
    offerRentalBreakdown(offer)?.customSolicitations ||
    offer?.metadata?.custom_solicitations ||
    offer?.metadata?.customSolicitations ||
    offer?.metadata?.adjustments ||
    [];
  if (Array.isArray(raw) && raw.length) {
    return raw.map(item =>
      typeof item === 'string'
        ? { type: 'Modificar', description: item }
        : {
            type: item.type || item.kind || item.action || 'Modificar',
            description:
              item.description ||
              item.label ||
              item.title ||
              JSON.stringify(item),
          }
    );
  }
  if (offer?.notes) {
    return [{ type: 'Modificar', description: offer.notes }];
  }
  return [];
};

export const rentalOfferPresentationRows = offer => {
  if (!isRentalOffer(offer)) return [];

  return [
    {
      label: 'Moradores',
      value:
        offerSummary(offer).residents_situation ||
        offerRentalBreakdown(offer)?.residents_situation ||
        residentsSituationFromValues(
          rentalMetadataNumber(offer, 'residents_count'),
          rentalMetadataBoolean(offer, 'has_pets')
        ),
    },
  ].filter(row => row.value);
};

export const offerActivityRows = offer => {
  if (!offer) return [];

  return [
    { label: 'Envio', value: formatDate(offer.submitted_at) },
    offer.decided_at
      ? { label: 'Decisão', value: formatDate(offer.decided_at) }
      : null,
    { label: 'Tempo', value: activityDurationLabel(offer) },
  ].filter(row => row?.value && row.value !== '—');
};

export const offerDecision = offer => offer?.metadata?.decision || {};

export const offerDecisionReason = offer =>
  offerDecision(offer)?.reason_label || '';

export const offerCanceledReason = offer => {
  if (!offer || !['declined', 'withdrawn'].includes(offer.status)) return '';
  if (offerSummary(offer).canceled_reason_label) {
    return offerSummary(offer).canceled_reason_label;
  }
  if (offerSummary(offer).status_message) {
    return offerSummary(offer).status_message;
  }

  const date = formatDate(offer.decided_at || offer.submitted_at);
  if (offer.status === 'declined') {
    return offerDecisionReason(offer)
      ? `${offerDecisionReason(offer)} em ${date}`
      : `Proprietário recusou em ${date}`;
  }
  if (isRentalOffer(offer)) return `Interessado desistiu de alugar em ${date}`;
  return `Interessado desistiu em ${date}`;
};

export const offerAcceptedSummary = offer => {
  if (!offer || offer.status !== 'accepted') return '';
  if (offerSummary(offer).accepted_at_label) {
    return offerSummary(offer).accepted_at_label;
  }
  if (offerSummary(offer).status_message) {
    return offerSummary(offer).status_message;
  }
  if (!offer.decided_at) return 'Proposta aceita';

  return `Proposta aceita em ${formatDate(offer.decided_at)}`;
};

export const offerPartnersRows = offer => {
  if (!offer) return [];

  const metadata = offer.metadata || {};
  const partners = metadata.partners || metadata.sale_partners || {};
  const negotiationName =
    partners.negotiation_executive_name ||
    metadata.negotiation_executive_name ||
    offer.user_name ||
    'Não informado';

  return [
    {
      label: 'Captador do Imóvel',
      value:
        partners.owner_agent_name ||
        metadata.owner_agent_name ||
        metadata.listing_owner_agent_name ||
        'Não informado',
    },
    {
      label: 'Corretor da visita',
      value:
        partners.visit_agent_name ||
        metadata.visit_agent_name ||
        offer.user_name ||
        'Não informado',
    },
    {
      label: 'Executivo de Negociação',
      value: negotiationName,
    },
  ];
};

export const decisionPriceSummary = offer => ({
  business_context: offer?.business_context || null,
  offer_amount: offer?.offer_amount || null,
  owner_receive_amount: ownerReceiveValue(offer),
  advertised_amount: advertisedValue(offer),
  admin_fee_value: isRentalOffer(offer) ? rentalAdminFeeForOffer(offer) : null,
  brokerage_fee_value: isRentalOffer(offer) ? null : brokerageFeeValue(offer),
  payment_kind: offer?.payment_kind || null,
  presentation: isRentalOffer(offer) ? offerPaymentLabel(offer) : null,
});
